import { useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface CopyButtonProps {
  value: string;
  label?: string;
  copiedLabel?: string;
  className?: string;
  size?: 'default' | 'sm';
}

export function CopyButton({
  value,
  label = 'Copy',
  copiedLabel = 'Copied!',
  className,
  size = 'default',
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Older browsers or insecure contexts (non-HTTPS). Silent fallback:
      // select-and-copy a hidden textarea. For our install wizard the page
      // is always HTTPS in practice, so this path is extremely rare.
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } finally {
        document.body.removeChild(ta);
      }
    }
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const Icon = copied ? Check : Copy;
  return (
    <Button
      type="button"
      variant="outline"
      size={size}
      onClick={handleCopy}
      className={cn('gap-2', copied && 'text-green-700', className)}
      aria-label={copied ? copiedLabel : label}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {copied ? copiedLabel : label}
    </Button>
  );
}
